<template>
  <form-row
    :label="label"
    :label-for="inputId"
    :error-message="errorMessage"
  >
    <template #label>
      <slot name="label" />
    </template>
    <template #above>
      <slot name="above" />
    </template>

    <div :class="cssClass">
      <v-select
        v-model="selected"
        :class="{'vs--error': errorMessage}"
        :reduce="reduce"
        :label="optionLabel"
        :options="options"
        :selectable="selectable"
        v-bind="$props"
        autocomplete="division-id"
        append-to-body
        :calculate-position="withPopper"
        :filter-by="filterMethod"
      >
        <template #option="option">
          <slot
            name="option"
            :option="option"
          />
        </template>
      </v-select>
    </div>

    <template #below>
      <slot name="below" />
    </template>
  </form-row>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import formRow from './form-row.vue';
import vSelect from 'vue-select';
import { createPopper } from "@popperjs/core";
import '../../style/vue-select.css';

interface SelectOption {
  value: string;
  label?: string;
  disabled?: boolean;
}

@Component({
  components: {'form-row': formRow, 'v-select': vSelect}
})
export default class SelectRow extends Vue {
  @Prop() readonly label!: string;
  @Prop({default: 'label'}) readonly optionLabel!: string;

  @Prop({default: ''}) readonly cssClass!: string;

  @Prop({default: false}) readonly disabled!: boolean;

  @Prop() readonly name!: string;

  @Prop() readonly value!: string;

  @Prop({default: () => []}) readonly options!: {label?: string, value: string};

  @Prop({default: () => (a: {value: string}) => a?.value}) readonly reduce!: (a: unknown) => unknown;

  @Prop() readonly id!: string;

  @Prop() readonly errorMessage!: string;

  @Prop({ default: true }) readonly allowEmpty!: boolean;

  @Prop( {default: () => (option: SelectOption) => !option.disabled }) selectable!: (option: SelectOption) => boolean;

  @Prop() readonly getOptionKey: any;

  @Prop({default: false}) readonly filterFromStart!: boolean;

  placement = 'top';

  withPopper(dropdownList: any, component: any, {width}: any) {
    /**
     * We need to explicitly define the dropdown width since
     * it is usually inherited from the parent with CSS.
     */
    dropdownList.style.width = width;
    /**
     * Here we position the dropdownList relative to the $refs.toggle Element.
     *
     * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
     * the dropdownList overlap by 1 pixel.
     *
     * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
     * wrapper so that we can set some styles for when the dropdown is placed
     * above.
     */
    const popper = createPopper(component.$refs.toggle, dropdownList, {
      placement: 'auto',
      modifiers: [
        {
          name: 'offset', options: {
            offset: [0, -1]
          }
        },
        {
          name: 'toggleClass',
          enabled: true,
          phase: 'write',
          fn ({state}) {
            component.$el.classList.toggle('drop-up', state.placement === 'top')
          },
        }]
    });

    /**
     * To prevent memory leaks Popper needs to be destroyed.
     * If you return function, it will be called just before dropdown is removed from DOM.
     */
    return () => popper.destroy();
  }

  get inputId(): string {
    return this.id !== '' ? this.id : this.name + '';
  }

  set selected(v: any) {
    this.$emit('input', v);
  }

  get selected(): any {
    return this.value;
  }

  filterMethod(option: {value: string, label: string}, label: string, search: string): any {
    if (this.filterFromStart) {
      return label.toLowerCase().indexOf(search.toLowerCase()) === 0;
    } else {
      return label.toLowerCase().indexOf(search.toLowerCase()) > -1;
    }
  }
}
</script>

<style scoped>

</style>
